import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  archiveCarrierAccount,
  archivePackagePreset,
  connectCarrierAccount,
  createPackagePreset,
  getStoreShippingSettings,
  listCarrierAccounts,
  listLabelsForOrder,
  listLabelsTop,
  listPackagePresets,
  purchaseShippingLabel,
  quoteShipping,
  refundShippingLabel,
  rotateCarrierApiKey,
  updatePackagePreset,
  upsertStoreShippingSettings,
  type ActiveContext,
  type ConnectCarrierInput,
  type DefaultParcel,
  type PackagePresetInput,
  type ShipFromAddress,
  type ShippingShipment,
} from '@pc/db';

const K = ['shipping'] as const;

export function useCarrierAccounts(ctx: ActiveContext) {
  return useQuery({
    queryKey: [...K, ctx.orgId, ctx.storeId, 'carrier-accounts'],
    queryFn: () => listCarrierAccounts(ctx),
  });
}

export function useConnectCarrierAccount(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ConnectCarrierInput) => connectCarrierAccount(ctx, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: K }),
  });
}

export function useRotateCarrierApiKey(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; apiKey: string }) =>
      rotateCarrierApiKey(ctx, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: K }),
  });
}

export function useArchiveCarrierAccount(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => archiveCarrierAccount(ctx, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: K }),
  });
}

export function useLabelsForOrder(ctx: ActiveContext, orderId: string | undefined) {
  return useQuery({
    queryKey: [...K, ctx.orgId, ctx.storeId, 'labels', 'order', orderId],
    queryFn: () => listLabelsForOrder(ctx, orderId as string),
    enabled: Boolean(orderId),
  });
}

export function useLabelsList(ctx: ActiveContext) {
  return useQuery({
    queryKey: [...K, ctx.orgId, ctx.storeId, 'labels', 'top'],
    queryFn: () => listLabelsTop(ctx),
  });
}

export function useQuoteShipping(ctx: ActiveContext) {
  return useMutation({
    mutationFn: (input: { carrierAccountId: string; shipment: ShippingShipment }) =>
      quoteShipping(ctx, input),
  });
}

// ---- package presets (OfficeHub shipping_presets parity) -----------------
// Org-scoped: presets describe physical boxes in the shared warehouse, so
// the key deliberately omits storeId.

export function usePackagePresets(ctx: ActiveContext) {
  return useQuery({
    queryKey: [...K, ctx.orgId, 'package-presets'],
    queryFn: () => listPackagePresets(ctx),
  });
}

export function useCreatePackagePreset(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PackagePresetInput) => createPackagePreset(ctx, input),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: [...K, ctx.orgId, 'package-presets'] }),
  });
}

export function useUpdatePackagePreset(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string } & Partial<PackagePresetInput>) =>
      updatePackagePreset(ctx, input),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: [...K, ctx.orgId, 'package-presets'] }),
  });
}

export function useArchivePackagePreset(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => archivePackagePreset(ctx, id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: [...K, ctx.orgId, 'package-presets'] }),
  });
}

export function usePurchaseShippingLabel(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      carrierAccountId: string;
      orderId: string;
      shipment: ShippingShipment;
      rateId: string;
      rateCents: number;
      rateCurrency?: string;
      chargedCents?: number;
      packagePresetId?: string | null;
    }) => purchaseShippingLabel(ctx, input),
    onSuccess: (_data, vars) => {
      // Invalidate shipping caches (labels) AND the orders queries so the
      // order detail page re-fetches the row whose shipping_cents and
      // total_cents just changed inside rpc_apply_label_cost_to_order.
      qc.invalidateQueries({ queryKey: K });
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['orders', vars.orderId] });
    },
  });
}

export function useStoreShippingSettings(ctx: ActiveContext) {
  return useQuery({
    queryKey: [...K, ctx.orgId, ctx.storeId, 'store-settings'],
    queryFn: () => getStoreShippingSettings(ctx),
  });
}

export function useUpsertStoreShippingSettings(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { shipFrom: ShipFromAddress; defaultParcel?: DefaultParcel | null }) =>
      upsertStoreShippingSettings(ctx, input),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: [...K, ctx.orgId, ctx.storeId, 'store-settings'] }),
  });
}

export function useRefundShippingLabel(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { carrierAccountId: string; labelId: string }) =>
      refundShippingLabel(ctx, input),
    // Refunds that the carrier confirms (SUCCESS) update orders.shipping_cents
    // via rpc_reverse_label_cost_from_order, so we invalidate both caches.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: K });
      qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}
